import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Connects to data-controller="column-actions"
//
// Handles column actions: add, duplicate, remove
// Works with Rails nested attributes pattern
//
// Usage:
//   button[
//     data-controller="column-actions"
//     data-action="click->column-actions#addColumn"
//     data-column-actions-column-name-value="field_123"
//   ]
//
export default class extends Controller {
  static values = {
    columnName: String,
    columnRef: String,
    columnType: String,
    columnTitle: String,
    columnIcon: String,
    typeClass: String,
    colorClass: String,
    cloneable: String,
    columnId: String,
    requiredParameters: Object,
    translatedParameters: Object,
    headerBg: String,
    headerText: String,
    columnBg: String,
    columnText: String,
    suggestedFormatCode: String
  }

  connect() {
    // Controller connected - logging disabled to reduce console noise
  }

  addColumn(event, insertAtIndex = null) {
    event.preventDefault()

    logger.log('[Column Actions] Adding column', {
      columnName: this.columnNameValue,
      columnType: this.columnTypeValue,
      insertAtIndex
    })

    // Find the selected columns list
    const selectedList = document.querySelector('#selectedColumnsList')
    if (!selectedList) {
      logger.error('[Column Actions] Selected columns list not found')
      return
    }

    // Generate unique ID for new column
    // CRITICAL: Use timestamp-only for Rails nested attributes compatibility
    // Rails accepts numeric string keys for new records (e.g., "1763241050570")
    // The "new_" prefix causes Rails to reject the nested attributes
    const newId = Date.now().toString()

    // ALWAYS add UID to column_ref to make it unique
    // This allows formulas to reference specific columns even if they have the same base name
    const baseColumnRef = this.columnRefValue
    const columnRefWithUid = `${baseColumnRef}_${newId}`
    
    logger.log('[Column Actions] Adding column with UID in column_ref:', baseColumnRef, '→', columnRefWithUid)

    // Create new column item HTML
    const newItem = this.createColumnItemHTML(newId, columnRefWithUid)

    // Remove "no columns" message if present
    const noColumnsMsg = selectedList.querySelector('.text-muted.text-center')
    if (noColumnsMsg) {
      noColumnsMsg.remove()
    }

    // Add to list at specified position or append to end
    if (insertAtIndex !== null && insertAtIndex >= 0) {
      const children = Array.from(selectedList.children)
      if (insertAtIndex < children.length) {
        // Insert before the element at insertAtIndex
        children[insertAtIndex].insertAdjacentHTML('beforebegin', newItem)
      } else {
        // Append to end if index is beyond current length
        selectedList.insertAdjacentHTML('beforeend', newItem)
      }
    } else {
      // No index specified, append to end
      selectedList.insertAdjacentHTML('beforeend', newItem)
    }

    // Update positions
    this.updatePositions()

    // Scroll into view
    const addedItem = selectedList.querySelector(`[data-column-id="${newId}"]`)
    if (addedItem) {
      addedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }

    // Dispatch event for preview controller
    this.dispatch('columnAdded', {
      detail: { columnId: newId }
    })

    // Notify selected-columns controller to update empty state
    // Dispatch from the selected list so it bubbles up to the panel-content controller
    const updateEvent = new CustomEvent('selected-columns:update', {
      bubbles: true,
      detail: { source: 'column-actions', columnId: newId }
    })
    selectedList.dispatchEvent(updateEvent)

    // Trigger auto-save
    const autoSaveEvent = new CustomEvent('report-autosave:schedule', {
      bubbles: true,
      detail: { source: 'column-actions', columnId: newId }
    })
    this.element.dispatchEvent(autoSaveEvent)
  }

  duplicateColumn(event) {
    event.preventDefault()

    // Try to find the original item in two ways:
    // 1. If button is inside the column item (inline button)
    let originalItem = this.element.closest('.selected-column-item-v3, .selected-column-item')

    // 2. If button is in properties panel, find by column ID
    if (!originalItem && this.hasColumnIdValue) {
      originalItem = document.querySelector(`#selected_column_${this.columnIdValue}`)
      logger.log('[Column Actions] Found column item by ID from properties panel:', this.columnIdValue)
    }

    if (!originalItem) {
      logger.error('[Column Actions] Original column item not found', {
        hasColumnIdValue: this.hasColumnIdValue,
        columnIdValue: this.columnIdValue,
        elementClasses: this.element.className
      })
      return
    }

    logger.log('[Column Actions] Duplicating column', {
      originalId: this.columnIdValue || originalItem.dataset.columnId
    })

    // Generate unique ID for duplicated column
    // CRITICAL: Use timestamp-only for Rails nested attributes compatibility
    const newId = Date.now().toString()

    // Clone the element
    const clone = originalItem.cloneNode(true)

    // Update IDs and names in clone
    this.updateClonedElement(clone, newId)

    // Insert after original
    originalItem.insertAdjacentElement('afterend', clone)

    // Update positions
    this.updatePositions()

    // Deselect the original column
    originalItem.classList.remove('selected')

    // Select the new column and open in properties panel
    clone.classList.add('selected')

    // Scroll into view
    clone.scrollIntoView({ behavior: 'smooth', block: 'nearest' })

    // Trigger the properties panel to open for the new column
    // Find the properties-sidepanel controller and call selectColumn
    const propertiesSidepanel = document.querySelector('[data-controller*="properties-sidepanel"]')
    if (propertiesSidepanel) {
      const controller = this.application.getControllerForElementAndIdentifier(
        propertiesSidepanel,
        'properties-sidepanel'
      )
      if (controller) {
        // Create a synthetic event to trigger selectColumn
        const syntheticEvent = {
          currentTarget: clone,
          params: { columnId: newId },
          preventDefault: () => {}
        }
        controller.selectColumn(syntheticEvent)
      }
    }

    // Dispatch event
    this.dispatch('columnDuplicated', {
      detail: {
        originalId: this.columnIdValue || originalItem.dataset.columnId,
        newId: newId
      }
    })

    // Notify selected-columns controller to update empty state
    const selectedList = document.querySelector('#selectedColumnsList')
    if (selectedList) {
      const updateEvent = new CustomEvent('selected-columns:update', {
        bubbles: true,
        detail: { source: 'column-actions-duplicate', columnId: newId }
      })
      selectedList.dispatchEvent(updateEvent)
    }

    // Trigger auto-save
    const autoSaveEvent = new CustomEvent('report-autosave:schedule', {
      bubbles: true,
      detail: { source: 'column-actions-duplicate', columnId: newId }
    })
    document.dispatchEvent(autoSaveEvent)
  }

  // Helper function to escape quotes in attribute values
  escapeAttr(text) {
    if (!text) return ''
    return String(text).replace(/"/g, '&quot;')
  }

  createColumnItemHTML(newId, columnRef = null) {
    // Clone the server-rendered template instead of building HTML string
    const template = document.querySelector('#column-item-template .column-item-template')
    if (!template) {
      logger.error('[Column Actions] Column item template not found!')
      return null
    }

    const clone = template.cloneNode(true)
    clone.classList.remove('column-item-template')

    // Use provided columnRef (with UID if duplicate) or fall back to the base value
    const finalColumnRef = columnRef || this.columnRefValue

    // Get metadata from data attributes
    let icon = this.hasColumnIconValue ? this.columnIconValue : 'database'
    let typeClass = this.hasTypeClassValue ? this.typeClassValue : 'type-data'
    let headerBg = this.hasHeaderBgValue ? this.headerBgValue : '#ffffff'
    let headerText = this.hasHeaderTextValue ? this.headerTextValue : '#000000'
    let columnBg = this.hasColumnBgValue ? this.columnBgValue : '#ffffff'
    let columnText = this.hasColumnTextValue ? this.columnTextValue : '#000000'

    // Check parent item if values not on controller
    const parentItem = this.element.closest('.column-item')
    if (parentItem) {
      icon = this.hasColumnIconValue ? icon : (parentItem.dataset.columnIcon || 'database')
      typeClass = this.hasTypeClassValue ? typeClass : (parentItem.dataset.typeClass || 'type-data')
      headerBg = this.hasHeaderBgValue ? headerBg : (parentItem.dataset.headerBg || '#ffffff')
      headerText = this.hasHeaderTextValue ? headerText : (parentItem.dataset.headerText || '#000000')
      columnBg = this.hasColumnBgValue ? columnBg : (parentItem.dataset.columnBg || '#ffffff')
      columnText = this.hasColumnTextValue ? columnText : (parentItem.dataset.columnText || '#000000')
    }

    const columnTitle = this.columnTitleValue || this.columnNameValue || 'Column'

    // Get sheet index
    let sheetIndex = this.getSheetIndex()

    // Get format code - use suggested format based on field type, or fall back to 'General'
    const formatCode = this.hasSuggestedFormatCodeValue ? this.suggestedFormatCodeValue : 'General'

    // Replace all placeholders in the cloned template
    const replacements = {
      '__COLUMN_ID__': newId,
      '__SHEET_INDEX__': sheetIndex,
      '__COLUMN_TYPE__': this.columnTypeValue,
      '__COLUMN_TITLE__': columnTitle,
      '__COLUMN_NAME__': this.columnNameValue,
      '__COLUMN_REF__': finalColumnRef,
      '__ICON__': icon,
      '__TYPE_CLASS__': typeClass,
      '__ORIGINAL_TITLE__': columnTitle,
      '__HEADER_BG__': headerBg,
      '__HEADER_TEXT__': headerText,
      '__COLUMN_BG__': columnBg,
      '__COLUMN_TEXT__': columnText,
      '__FORMAT_CODE__': formatCode,
      '__FORMULA_VALUE__': ''  // Empty for new Formula columns - user will fill in properties panel
    }

    // Replace in attributes and text content
    this.replacePlaceholders(clone, replacements)

    // Generate and append parameter inputs if needed
    if (this.hasRequiredParametersValue && this.requiredParametersValue) {
      const paramHtml = this.generateParameterInputs(newId, sheetIndex)
      if (paramHtml) {
        const paramContainer = clone.querySelector('[data-column-parameters]')
        if (paramContainer) {
          paramContainer.innerHTML = paramHtml
        }
      }
    }

    return clone.outerHTML
  }

  getSheetIndex() {
    // Get the sheet index from existing columns or hidden fields
    const existingColumn = document.querySelector('#selectedColumnsList .selected-column-item-v3')
    if (existingColumn) {
      const existingField = existingColumn.querySelector('input[name*="[report_sheets_attributes]"]')
      if (existingField) {
        const match = existingField.name.match(/\[report_sheets_attributes\]\[([^\]]+)\]/)
        if (match) {
          logger.log('[Column Actions] Using sheet index from existing column:', match[1])
          return match[1]
        }
      }
    }

    // Fallback: find from form's sheet hidden fields
    const selectedList = document.querySelector('#selectedColumnsList')
    if (selectedList) {
      const fieldsForContainer = selectedList.closest('[data-controller*="report-form-v3"]')
      if (fieldsForContainer) {
        const allSheetFields = fieldsForContainer.querySelectorAll('input[name*="[report_sheets_attributes]"][name$="[id]"]')
        if (allSheetFields.length > 0) {
          const currentSheetField = allSheetFields[allSheetFields.length - 1]
          const match = currentSheetField.name.match(/\[report_sheets_attributes\]\[([^\]]+)\]/)
          if (match) {
            logger.log('[Column Actions] Using sheet index from form:', match[1])
            return match[1]
          }
        }
      }
    }

    logger.warn('[Column Actions] Could not determine sheet index, using fallback')
    return '0'
  }

  replacePlaceholders(element, replacements) {
    // Replace in all attributes
    Array.from(element.attributes).forEach(attr => {
      let value = attr.value
      Object.entries(replacements).forEach(([placeholder, replacement]) => {
        value = value.replace(new RegExp(placeholder, 'g'), replacement)
      })
      attr.value = value
    })

    // Replace in text content (but not in child elements)
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false)
    let node
    while (node = walker.nextNode()) {
      Object.entries(replacements).forEach(([placeholder, replacement]) => {
        node.textContent = node.textContent.replace(new RegExp(placeholder, 'g'), replacement)
      })
    }

    // Replace in child elements recursively
    Array.from(element.children).forEach(child => {
      this.replacePlaceholders(child, replacements)
    })
  }

  generateParameterInputs(columnId, sheetIndex) {
    // Check if requiredParametersValue exists (don't use hasRequiredParametersValue as it's false for empty objects)
    if (!this.requiredParametersValue || typeof this.requiredParametersValue !== 'object') {
      return ''
    }

    const params = this.requiredParametersValue
    const translatedParams = this.hasTranslatedParametersValue ? this.translatedParametersValue : {}
    
    if (Object.keys(params).length === 0) {
      return ''
    }

    let html = ''
    for (const [paramName, paramTypeOrHash] of Object.entries(params)) {
      const inputName = `report[report_sheets_attributes][${sheetIndex}][report_columns_attributes][${columnId}][parameters][${paramName}]`
      
      // Handle both simple types (e.g., :date) and complex types (e.g., { type: :select, options: [...], default: ... })
      let paramType, paramOptions, paramDefault
      if (typeof paramTypeOrHash === 'object' && paramTypeOrHash !== null) {
        paramType = paramTypeOrHash.type
        paramOptions = paramTypeOrHash.options
        paramDefault = paramTypeOrHash.default
      } else {
        paramType = paramTypeOrHash
        paramOptions = null
        paramDefault = null
      }

      // Get translated data if available (may also contain default value and option_types)
      const translatedData = translatedParams[paramName] || {}
      const paramLabel = translatedData.label || paramName
      const optionLabels = translatedData.option_labels || []
      const optionTypes = translatedData.option_types || null
      // Use default from translated data if available, otherwise from original hash
      if (paramDefault === null || paramDefault === undefined) {
        paramDefault = translatedData.default
      }

      // Convert default value to string for input field
      const defaultValue = paramDefault !== null && paramDefault !== undefined
        ? (Array.isArray(paramDefault) ? JSON.stringify(paramDefault) : String(paramDefault))
        : ''

      if (paramOptions) {
        const dataAttrs = [
          `data-column-parameter="${paramName}"`,
          `data-column-parameter-type="${paramType}"`,
          `data-column-parameter-label="${this.escapeAttr(paramLabel)}"`,
          `data-column-parameter-options='${JSON.stringify(paramOptions)}'`
        ]
        if (optionLabels.length > 0) {
          dataAttrs.push(`data-column-parameter-option-labels='${JSON.stringify(optionLabels)}'`)
        }
        if (paramDefault !== null && paramDefault !== undefined) {
          const defaultJson = Array.isArray(paramDefault) ? JSON.stringify(paramDefault) : String(paramDefault)
          dataAttrs.push(`data-column-parameter-default='${this.escapeAttr(defaultJson)}'`)
        }
        // Include option_types for format code suggestions (e.g., start_date: 'date')
        if (optionTypes) {
          dataAttrs.push(`data-column-parameter-option-types='${JSON.stringify(optionTypes)}'`)
        }
        html += `<input type="hidden" name="${inputName}" value="${this.escapeAttr(defaultValue)}" ${dataAttrs.join(' ')}>`
      } else {
        const extraAttrs = (paramDefault !== null && paramDefault !== undefined)
          ? ` data-column-parameter-default="${this.escapeAttr(String(paramDefault))}"`
          : ''
        html += `<input type="hidden" name="${inputName}" value="${this.escapeAttr(defaultValue)}" data-column-parameter="${paramName}" data-column-parameter-type="${paramType}" data-column-parameter-label="${this.escapeAttr(paramLabel)}"${extraAttrs}>`
      }
    }
    
    return html
  }

  updateClonedElement(clone, newId) {
    // Update data attributes
    clone.dataset.columnId = newId
    clone.id = `selected_column_${newId}`

    clone.setAttribute('data-properties-sidepanel-column-id-param', newId)

    // CRITICAL: Remove the database ID field so Rails treats this as a NEW record
    const idInput = clone.querySelector('input[name*="[id]"]')
    if (idInput) {
      logger.log('[Column Actions] Removing database ID from duplicated column to force new record creation')
      idInput.remove()
    }

    // Update all input names to use new ID
    const inputs = clone.querySelectorAll('input, select, textarea')
    inputs.forEach(input => {
      const name = input.name
      if (name) {
        // Replace the ID part of the name (e.g., [123] -> [1763241050570])
        const newName = name.replace(/\[report_columns_attributes\]\[\d+\]/, `[report_columns_attributes][${newId}]`)
        input.name = newName
      }
    })

    // CRITICAL: Update column_ref to be unique
    // This allows formulas to reference specific columns even when duplicated
    const columnRefInput = clone.querySelector('input[data-column-data="column_ref"]')
    if (columnRefInput) {
      const originalRef = columnRefInput.value
      // Append the new ID to make it unique (same pattern as addColumn)
      const newColumnRef = `${originalRef}_${newId}`
      columnRefInput.value = newColumnRef
      columnRefInput.setAttribute('value', newColumnRef)
      logger.log(`[Column Actions] Updated column_ref from ${originalRef} to ${newColumnRef}`)
    }

    // Clear _destroy flag
    const destroyInput = clone.querySelector('input[name*="[_destroy]"]')
    if (destroyInput) {
      destroyInput.value = 'false'
    }
  }

  updatePositions() {
    const items = document.querySelectorAll('#selectedColumnsList .selected-column-item-v3')

    // Update positions based on VISIBLE order, skipping destroyed columns
    let visibleIndex = 0
    items.forEach((item) => {
      const destroyField = item.querySelector('input[name*="[_destroy]"]')
      const isDestroyed = destroyField && destroyField.value === 'true'

      const positionInput = item.querySelector('input[name*="[position]"]')
      if (positionInput) {
        if (isDestroyed) {
          positionInput.value = 99999
        } else {
          positionInput.value = visibleIndex
          visibleIndex++
        }
      }
    })

    logger.log('[Column Actions] Positions updated', { visible: visibleIndex, total: items.length })
  }
}


